import { MenuProps } from '@mui/base/Menu';
import {
  cloneElement,
  HTMLAttributes,
  isValidElement,
  MouseEvent,
  PropsWithChildren,
  ReactElement,
  useId,
  useState,
} from 'react';
import DropMenuItems, { DropMenuItem } from '@app/components/UI/DropMenu/DropMenuItems';
import { findSlotWithName, withSlotName } from '@app/utils/slots';
import tw from 'twin.macro';

interface Props extends MenuProps {
  children: [
    // Unfortunately, not working as expected with JSX (but let's keep for doc purposes):
    // https://react-typescript-cheatsheet.netlify.app/docs/advanced/patterns_by_usecase#what-you-cannot-do
    ReactElement<unknown, typeof Trigger>,
    ReactElement<unknown, typeof Items>,
  ]
}

/**
 * @see https://mui.com/base-ui/react-menu/#usage For implementation details.
 */
function DropMenu({
  children,
}: Props) {
  const id = useId();
  const [anchorEl, setAnchorEl] = useState<EventTarget|null>(null);
  const isOpened = Boolean(anchorEl);

  const handleClick = (event: MouseEvent) => setAnchorEl(anchorEl ? null : event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const DropMenuTriggerElement = findSlotWithName(children, Trigger);

  if (!DropMenuTriggerElement) {
    throw new Error('You MUST provide a <DropMenu.Trigger> component as a child of <DropMenu>.');
  }

  const TriggerElement = cloneElement(DropMenuTriggerElement, {
    onClick: (e) => {
      DropMenuTriggerElement.props.onClick?.(e);
      handleClick(e);
    },
    'aria-controls': isOpened ? id : undefined,
    'aria-expanded': isOpened ? 'true' : 'false',
    'aria-haspopup': 'menu',
  });

  const DropMenuItemsElement = findSlotWithName(children, Items);

  if (!DropMenuItemsElement) {
    throw new Error('You MUST provide a <DropMenu.Items> component as a child of <DropMenu>.');
  }

  return <StyledDropMenu>
    {/* Element where and by which the drop menu is triggered  */}
    {TriggerElement}
    {/* The rendered elements on showing the drop menu  */}
    <DropMenuItems.Slot
      id={id}
      {...DropMenuItemsElement.props}
      anchorEl={anchorEl}
      close={handleClose}
      isOpened={isOpened}
    >
      {DropMenuItemsElement}
    </DropMenuItems.Slot>
  </StyledDropMenu>;
}

const StyledDropMenu = tw.div`relative p-0 m-0`;

export function DropMenuTrigger({ children, ...remainingProps }: PropsWithChildren<HTMLAttributes<unknown>>) {
  if (!isValidElement(children)) {
    throw new Error('DropMenu.Trigger children must be a valid React Element');
  }

  return cloneElement(children, { ...remainingProps });
}

const Items = withSlotName(DropMenuItems, 'DropMenu.Items');
const Trigger = withSlotName(DropMenuTrigger, 'DropMenu.Trigger');

// https://react-typescript-cheatsheet.netlify.app/docs/advanced/misc_concerns#namespaced-components
export default Object.assign(DropMenu, {
  Trigger,
  Items,
  Item: withSlotName(DropMenuItem, 'DropMenu.Item'),
});
